import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { projectsAPI } from "../lib/api";
import { PROJECT_TYPES, projectTypeLabel, PROJECT_STATUS_STYLES, formatAUDAmount } from "../lib/projects";
import { ProjectDetail } from "../components/projects/ProjectDetail";
import { FolderKanban, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", project_type: "" });
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const loadProjects = useCallback(async () => {
    try {
      const res = await projectsAPI.getAll();
      setProjects(res.data);
    } catch (err) {
      toast.error("Failed to load projects");
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.project_type) {
      toast.error("Enter a name and choose a project type");
      return;
    }
    setCreating(true);
    try {
      const res = await projectsAPI.create(form);
      toast.success("Project created");
      setCreateOpen(false);
      setForm({ name: "", project_type: "" });
      await loadProjects();
      setSelectedId(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  if (selectedId) {
    return <ProjectDetail projectId={selectedId} onBack={() => { setSelectedId(null); loadProjects(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <FolderKanban className="w-7 h-7 text-blue-600" />
            Projects
          </h1>
          <p className="text-slate-600 mt-1">Create and manage projects, invite external collaborators</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="create-project-button">
          <Plus className="w-4 h-4 mr-2" /> New Project
        </Button>
      </div>

      {projects.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-slate-500" data-testid="projects-empty-state">
            <FolderKanban className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            No projects yet. Create your first project to get started.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map((p) => (
            <Card
              key={p.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setSelectedId(p.id)}
              data-testid={`project-card-${p.id}`}
            >
              <CardHeader className="pb-2 flex flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle className="text-base">{p.name}</CardTitle>
                  <CardDescription>{projectTypeLabel(p.project_type)}</CardDescription>
                </div>
                <Badge className={`${PROJECT_STATUS_STYLES[p.status]} hover:bg-opacity-100 capitalize`}>{p.status}</Badge>
              </CardHeader>
              <CardContent className="text-sm text-slate-600 space-y-1">
                <p>Cost: <b>{formatAUDAmount(p.cost)}</b></p>
                <p>Paid: <b>{formatAUDAmount((p.payments || []).reduce((s, x) => s + (x.amount || 0), 0))}</b> ({(p.payments || []).length} payment{(p.payments || []).length === 1 ? "" : "s"})</p>
                <p className="text-xs text-slate-400">Created by {p.created_by} • {new Date(p.created_date).toLocaleDateString("en-AU")}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-sm" data-testid="create-project-dialog">
          <DialogHeader>
            <DialogTitle>New Project</DialogTitle>
            <DialogDescription>Give the project a name and choose its type.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Project Name</Label>
              <Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name-input" />
            </div>
            <div>
              <Label className="text-sm">Type of Project</Label>
              <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                <SelectTrigger className="mt-1" data-testid="project-type-select">
                  <SelectValue placeholder="Choose type..." />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-blue-600 hover:bg-blue-700" data-testid="project-create-submit">
              {creating ? "Creating..." : "Create Project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
